import './WorkspaceListSearch.scss';
import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { ITeamMember } from '../../team/models/TeamMember';
import { IUserService } from '../../user/user.service';
import { ChangeWorkspaceOwnerModal } from '../components/ChangeWorkspaceOwnerModal';
import { DeleteWorkspaceConfirmationModal } from '../components/DeleteWorkspaceConfirmationModal';
import { RenameWorkspaceModal } from '../components/RenameWorkspaceModal';
import { WorkspacesList } from '../components/WorkspacesList';
import { UpdateWorkspaceTimeZoneModalContainer } from '../sub/UpdateWorkspaceTimeZone/containers/UpdateWorkspaceTimeZoneModal.container';
import { IUpdateWorkspaceTimeZoneBl } from '../sub/UpdateWorkspaceTimeZone/UpdateWorkspaceTimeZone.bl';
import { IWorkspaceService } from '../workspaces.service';

export const WorkspacesListContainer = observer((): ReactElement => {
  const workspaceService = useInjection(IWorkspaceService.$);
  const userService = useInjection(IUserService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const updateWorkspaceTimeZoneBl = useInjection(IUpdateWorkspaceTimeZoneBl.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const context = useContext(Settings);

  const [showRenameWorkspaceModal, setShowRenameWorkspaceModal] = useState(false);
  const [renameWorkspaceName, setRenameWorkspaceName] = useState('');
  const [renameWorkspaceInputStatus, setRenameWorkspaceInputStatus] = useState(InputStatus.empty());
  const [workspaceToRename, setWorkspaceToRename] = useState('');

  const [deleteWorkspaceModalShown, setDeleteWorkspaceModalShown] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState('');

  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerEmailStatus, setOwnerEmailStatus] = useState(InputStatus.empty());
  const [workspaceTeamMembers, setWorkspaceTeamMembers] = useState<ITeamMember[]>([]);
  const [workspaceToChangeOwner, setWorkspaceToChangeOwner] = useState('');

  useEffect(() => {
    void workspaceService.initWorkspaceListAsync();
    return (): void => {
      workspaceService.disposeWorkspaceListAsync();
    };
  }, []);

  const toggleRenameWorkspaceModal = (workspaceId: string): void => {
    if (isLoading) {
      return;
    }

    const workspace = workspaceService.store.workspaces.find((w) => w.id === workspaceId);
    setShowRenameWorkspaceModal((prevState) => !prevState);
    setRenameWorkspaceName(workspace ? workspace.name : '');
    setRenameWorkspaceInputStatus(InputStatus.empty());
    setWorkspaceToRename(workspaceId);
  };

  const toggleChangeOwnerModal = (workspaceId: string): void => {
    workspaceService.store.takeOverWorkspaceLimits = undefined;
    const listItem = workspaceService.store.workspaceListItems.find((w) => w.id === workspaceId);
    setShowChangeOwnerModal((prevState) => !prevState);
    setOwnerEmail('');
    setOwnerEmailStatus(InputStatus.empty());
    setWorkspaceTeamMembers(listItem ? listItem.members : []);
    setWorkspaceToChangeOwner(workspaceId);
  };

  const toggleDeleteModal = (workspaceId: string): void => {
    setDeleteWorkspaceModalShown((prevState) => !prevState);
    setWorkspaceToDelete(workspaceId);
  };

  const toggleUpdateWorkspaceTimeZoneModal = (workspaceId: string, timeZoneOffset: number): void => {
    updateWorkspaceTimeZoneBl.showForm(workspaceId, timeZoneOffset);
  };

  const handleRenameWorkspaceNameChange = (value: string): void => {
    setRenameWorkspaceName(value);
    setRenameWorkspaceInputStatus(validateWorkspaceName(value));
  };

  const validateWorkspaceName = (value: string): InputStatus => {
    const errorCodes: TranslationKey[] = [];

    if (value.trim() === '') {
      errorCodes.push('field_cant_be_empty');
    }

    const isNotUnique = workspaceService.store.workspaces.map((w) => w.name.toLocaleUpperCase()).includes(value.trim().toLocaleUpperCase());

    if (isNotUnique) {
      errorCodes.push('DUPLICATE_WORKSPACE_NAME');
    }

    return InputStatus.buildFrom(errorCodes);
  };

  const renameWorkspace = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      if (renameWorkspaceInputStatus.isValid !== true) {
        return;
      }
      const result = await workspaceService.renameWorkspaceAsync(workspaceToRename, renameWorkspaceName);

      if (result instanceof ACDLError) {
        setRenameWorkspaceInputStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? []));
      } else {
        setRenameWorkspaceInputStatus(InputStatus.empty());
        setShowRenameWorkspaceModal(false);
      }
    }, 'rename-workspace-button');
  };

  const handleChangeOwnerConfirmedAsync = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      if (ownerEmailStatus.isValid) {
        const result = await workspaceService.changeOwnerAsync(ownerEmail, workspaceToChangeOwner);

        if (result instanceof ACDLError && result.apiErrorResponse?.errorCodes?.includes('CAN_NOT_TAKEOVER_WORKSPACE')) {
          workspaceService.store.takeOverWorkspaceLimits = undefined;
          await workspaceService.getTakeOverWorkspaceLimitsAsync(ownerEmail);
        } else {
          toggleChangeOwnerModal('');
          await userService.getUserInfoAsync();
        }
      }
    }, 'change-workspace-owner-button');
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      await workspaceService.deleteWorkspaceAsync(workspaceToDelete);
      setDeleteWorkspaceModalShown(false);
    }, 'delete-workspace-button');
  };

  const handleOwnerEmailChange = async (email: string): Promise<void> => {
    workspaceService.store.takeOverWorkspaceLimits = undefined;
    setOwnerEmail(email);
    setOwnerEmailStatus(InputStatus.valid());
    await workspaceService.getTakeOverWorkspaceLimitsAsync(email);
  };

  const handleOrderingChange = async (orderBy: string, orderType: string): Promise<void> => {
    workspaceService.store.workspacesPaging.orderBy = orderBy;
    workspaceService.store.workspacesPaging.orderType = orderType;
    await withLoaderAsync(async () => workspaceService.getWorkspaceListItemsAsync());
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    workspaceService.store.workspacesPaging.pageNumber = pageNumber;
    workspaceService.store.workspacesPaging.pageSize = pageSize;
    context.setWorkspacesPageSize(pageSize);
    await withLoaderAsync(async () => workspaceService.getWorkspaceListItemsAsync());
  };

  const toggleRenameWorkspaceModalWithEmpty = (): void => {
    if (isLoading) {
      return;
    }
    toggleRenameWorkspaceModal('');
  };

  const toggleDeleteModalWithEmpty = (): void => {
    if (isLoading) {
      return;
    }
    toggleDeleteModal('');
  };

  const toggleChangeOwnerModalWithEmpty = (): void => {
    if (isLoading) {
      return;
    }
    toggleChangeOwnerModal('');
  };

  return (
    <>
      <RenameWorkspaceModal
        showRenameWorkspace={showRenameWorkspaceModal}
        renameWorkspaceName={renameWorkspaceName}
        renameWorkspaceNameStatus={renameWorkspaceInputStatus}
        toggleRenameWorkspace={toggleRenameWorkspaceModalWithEmpty}
        onNameChange={handleRenameWorkspaceNameChange}
        onRenameSave={renameWorkspace}
      />
      <DeleteWorkspaceConfirmationModal
        show={deleteWorkspaceModalShown}
        onConfirm={handleConfirmDelete}
        onCancel={toggleDeleteModalWithEmpty}
      />
      <ChangeWorkspaceOwnerModal
        showChangeOwner={showChangeOwnerModal}
        ownerEmail={ownerEmail}
        ownerEmailStatus={ownerEmailStatus}
        users={workspaceTeamMembers}
        takeoverWorkspaceLimits={workspaceService.store.takeOverWorkspaceLimits}
        onOwnerEmailChange={handleOwnerEmailChange}
        toggleOwnerModal={toggleChangeOwnerModalWithEmpty}
        onOwnerSave={handleChangeOwnerConfirmedAsync}
      />
      <UpdateWorkspaceTimeZoneModalContainer />
      <WorkspacesList
        isLoading={isLoading || workspaceService.store.showLoader}
        currentWorkspaceId={currentWorkspaceStore.currentWorkspace.id}
        workspaces={workspaceService.store.workspaceListItems}
        onOrderingChange={handleOrderingChange}
        onPaginationChange={handlePaginationChange}
        pagination={{ ...workspaceService.store.workspacesPaging, pageSize: context.store.workspacesPageSize }}
        toggleChangeOwnerOnList={toggleChangeOwnerModal}
        toggleDeleteWorkspaceOnList={toggleDeleteModal}
        toggleRenameWorkspaceOnList={toggleRenameWorkspaceModal}
        toggleUpdateWorkspaceTimeZoneOnList={toggleUpdateWorkspaceTimeZoneModal}
        sorting={{
          orderBy: workspaceService.store.workspacesPaging.orderBy || '',
          orderType: workspaceService.store.workspacesPaging.orderType || '',
        }}
      />
    </>
  );
});
